import cn from 'classnames';
import { type ReactNode, useRef, useState } from 'react';
import { Button, type ButtonProperties } from '../button';
import { Icon } from '../icons';
import { useIsButtonVisible, useIsExpandableEffect, useIsExpandedRestoreEffect } from './hooks';
import styles from './read-more.module.css';

export type CollapsibleHeight = 'middle' | 'small';

export type ReadMoreCollapseHeight = CollapsibleHeight | number;

export type ReadMoreFade = 'light' | 'primary' | 'white';

type P = {
  allowCollapse?: boolean;
  buttonPaddingBottom?: boolean;
  buttonType?: ButtonProperties['type'];
  children: ReactNode;
  className?: string;
  collapse?: {
    enable?: boolean;
    height?: ReadMoreCollapseHeight;
  };
  fade?: ReadMoreFade;
};

const heightOptions: Record<CollapsibleHeight, number> = {
  middle: 480,
  small: 200,
};

const getCollapsedHeight = (value: ReadMoreCollapseHeight) =>
  typeof value === 'number' ? value : heightOptions[value];

const defaultProps = {};

const ReadMore = ({
  buttonPaddingBottom = true,
  buttonType = 'link-primary',
  children,
  className,
  collapse: { enable = true, height = 'middle' } = defaultProps,
  fade = 'white',
}: P) => {
  const collapsedHeight = getCollapsedHeight(height);
  const contentReference = useRef<HTMLDivElement | null>(null);
  const [isExpandable, setIsExpandable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isButtonVisible = useIsButtonVisible({ enable, isExpandable, isExpanded });

  useIsExpandedRestoreEffect({ isExpandable, isExpanded, setIsExpanded });
  useIsExpandableEffect({ contentReference, maxHeight: collapsedHeight, setIsExpandable });

  return (
    <div
      className={cn(styles['read-more'], className, {
        [styles.collapsible ?? '']: isExpanded && isButtonVisible,
        [styles.expandable ?? '']: isExpandable,
        [styles.expanded ?? '']: isExpanded,
        [styles['fade-light'] ?? '']: fade === 'light',
        [styles['fade-primary'] ?? '']: fade === 'primary',
        [styles['fade-white'] ?? '']: fade === 'white',
      })}
      style={{
        ['--read-more-collapsed-max' as string]: `${collapsedHeight}px`,
      }}
    >
      <div ref={contentReference}>{children}</div>
      {isButtonVisible ? (
        <Button
          className={cn(styles.button, {
            [styles['button--padding-bottom'] ?? '']: buttonPaddingBottom,
          })}
          icon={<Icon icon="down-line" rotate={isExpanded ? 180 : 0} />}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
          size="large"
          type={buttonType}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </div>
  );
};

export default ReadMore;
